import { useEffect } from 'react'

import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { Button, Label, RadioGroup, RadioGroupItem } from '@/components/ui'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/brasil/components/layouts'
import { transformarCompradorPFListagemParaFormulario } from '@/features/proposta/brasil/domain/Comprador/form-to-listagem/mapper-comprador-listagem-para-formulario'
import type { TPropostaSchemaInput } from '@/features/proposta/brasil/domain/schemas'
import type {
  CompradorPessoaFisicaStepSchemaInput,
  CompradorPessoaFisicaStepSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import { Cpf } from '@/utils/cpf'

type TSelecaoCompradorMenorVinculo = {
  clientes: TPropostaSchemaInput['cadastroCompradores']['clientes']
  compradores: TCompradoresCadastrados
  onBack: () => void
  onNext: () => void
  emailsJaVinculados: Record<string, string>
}

export const VincularCompradorMenor = ({
  clientes,
  compradores,
  onBack,
  onNext,
  emailsJaVinculados,
}: TSelecaoCompradorMenorVinculo) => {
  const { trigger, reset, watch } = useFormContext<
    CompradorPessoaFisicaStepSchemaInput,
    unknown,
    CompradorPessoaFisicaStepSchemaOutput
  >()
  const compradorMenorId = watch('dados.id')
  const options = clientes?.filter((comprador) => !comprador.maiorDe18Anos).sort((a, b) => a.nome.localeCompare(b.nome))

  const handleValidateOnNext = async () => {
    const isValid = await trigger('dados.cpf')
    if (isValid) {
      onNext()
    }
  }

  const handlePreencherMenorSeApenasUm = () => {
    if (options.length === 1 && options[0]) {
      const unicoMenor = options[0]
      reset({
        ...transformarCompradorPFListagemParaFormulario(
          unicoMenor,
          compradores,
          clientes,
          'create',
          emailsJaVinculados,
          'MaePai',
        ),
        fluxoIndependente: 'MaePai',
      })
    }
  }

  useEffect(() => {
    handlePreencherMenorSeApenasUm()
  }, [])

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>
          {t('proposta:cadastro_compradores.steps.titulo_vinculo_mae_pai')}
        </StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="grid pt-3">
          <RadioGroup
            className="flex flex-col gap-6"
            value={compradorMenorId}
            onValueChange={(v) => {
              const comprador = options.find((option) => option.id === v)!
              if (comprador) {
                reset({
                  ...transformarCompradorPFListagemParaFormulario(
                    comprador,
                    compradores,
                    clientes,
                    'create',
                    emailsJaVinculados,
                    'MaePai',
                  ),
                  fluxoIndependente: 'MaePai',
                })
              }
            }}
          >
            {options.map((option) => (
              <div key={option.cpf} className="flex items-center gap-2">
                <RadioGroupItem value={option.id} id={option.cpf} />
                <Label className="font-nunito grid font-semibold" htmlFor={option.cpf}>
                  {option.nome}
                  <span className="font-normal">CPF: {new Cpf(option.cpf).format()}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12 w-full md:w-auto" variant="outline" onClick={onBack}>
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button className="font-nunito h-12 w-full md:w-27" onClick={handleValidateOnNext}>
          {t('proposta:cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
